import React, { useState } from 'react';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import Link from './Link';

export type ColoredTagProps = {
	color?: string | null;
	containerXcss?: ReturnType<typeof cssMap>[string];
	contentXcss?: ReturnType<typeof cssMap>[string];
	elemBefore?: React.ReactNode;
	href?: string;
	labelName: string;
	openLinksInNewTab?: boolean;
};

const styles = cssMap({
	container: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: token('space.050'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small', '3px'),
		paddingInline: token('space.050'),
		paddingBlock: token('space.0'),
		marginBlock: token('space.050'),
		marginInline: token('space.050'),
		height: '20px',
		maxWidth: '100%',
		overflow: 'hidden',
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
	text: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		maxWidth: '180px',
	},
});

const ColoredTag = ({
	labelName,
	href,
	color,
	containerXcss,
	contentXcss,
	openLinksInNewTab,
	elemBefore,
}: ColoredTagProps): React.JSX.Element => {
	const [hovered, setHovered] = useState(false);

	const isLink = Boolean(href);

	const Inner = (
		<Box
			as="span"
			xcss={styles.container}
			onMouseEnter={isLink ? () => setHovered(true) : undefined}
			onMouseLeave={isLink ? () => setHovered(false) : undefined}
			style={{
				borderColor: color || undefined,
				backgroundColor:
					isLink && hovered ? (token('color.background.neutral.hovered') as string) : undefined,
				textDecoration: isLink ? 'none' : undefined,
			}}
		>
			{elemBefore}
			<Box
				as="span"
				xcss={contentXcss ?? styles.text}
				style={{ textDecoration: isLink && hovered ? 'underline' : undefined }}
			>
				{contentXcss ? (
					<Box as="span" xcss={styles.text}>
						{labelName}
					</Box>
				) : (
					labelName
				)}
			</Box>
		</Box>
	);

	const WithContainer = containerXcss ? (
		<Box as="span" xcss={containerXcss}>
			{Inner}
		</Box>
	) : (
		Inner
	);

	if (href) {
		return (
			<Link href={href} openLinksInNewTab={openLinksInNewTab}>
				{WithContainer}
			</Link>
		);
	}
	return WithContainer;
};

export default ColoredTag;
